import { CalendarClockIcon, CheckIcon, WalletIcon } from 'lucide-react';
import { formatUsd, minDownPaymentLabel } from 'shared';
import { PriceDisplay } from '@/components/price-display.js';
import { FieldFrame } from '@/components/field.js';
import { defaultDownPaymentInput } from '@/lib/finance.js';
import { paymentTermsLabel } from '@/lib/formatters.js';
import { financeLimits } from '@/state/config.js';
import { setCartEntryPayment } from '@/state/session.js';
import { findCartLine } from '@/state/pricing.js';
import { showPaymentErrors } from '@/flows/outreach/state/payment-form.js';
import { TermChipRow } from '@/flows/outreach/components/term-chip-row.js';

interface OutreachPaymentCardProps {
  cartIndex: number;
  /**
   * False once this plan's payment is confirmed. Editing a settled card would reprice it
   * behind the flow's back, and a down payment invalidated after the fact would surface
   * only as a failed checkout.
   */
  editable: boolean;
}

/**
 * How the customer pays for ONE product. The thread carries one of these per plan, so it
 * never shows cart-wide totals — those belong to the summary card at the end.
 */
export function OutreachPaymentCard({ cartIndex, editable }: OutreachPaymentCardProps) {
  const line = findCartLine(cartIndex);
  // A re-rate can retire the plan while its card sits in the thread.
  if (line === undefined) return null;

  if (!editable) {
    return (
      <div className="sm-panel sm-animate-in sm-chat-tail sm-chat-settled">
        <span className="sm-stack sm-pair">
          <span className="sm-type-body-strong">{line.tier.product_name}</span>
          <span className="sm-type-caption sm-tabular">{paymentTermsLabel(line)}</span>
        </span>
      </div>
    );
  }

  const {
    entry,
    durationOptions,
    canFinance,
    minDown,
    monthly,
    pifWholesale,
    financeWholesale,
    downPaymentError,
  } = line;
  const isFinance = entry.payment.type === 'finance';
  const showErrors = showPaymentErrors.value;
  const longestTerm = durationOptions[durationOptions.length - 1];

  function selectFinance() {
    // Opens on the longest term offered and the minimum down payment, so the customer
    // starts on a combination that is already valid.
    if (longestTerm === undefined) return;
    setCartEntryPayment(cartIndex, {
      type: 'finance',
      durationMonths: longestTerm,
      downPaymentInput: defaultDownPaymentInput(financeWholesale, financeLimits.value),
    });
    showPaymentErrors.value = false;
  }

  return (
    <div className="sm-panel sm-stack sm-animate-in sm-chat-tail sm-chat-form-card">
      <div className="sm-row-2-stack">
        <button
          type="button"
          className="sm-focus-ring sm-paytype sm-paytype-row"
          aria-pressed={!isFinance}
          onClick={() => {
            setCartEntryPayment(cartIndex, { type: 'pay_in_full' });
            showPaymentErrors.value = false;
          }}
        >
          <span className="sm-center sm-paytype-icon">
            <WalletIcon size={18} aria-hidden="true" />
          </span>
          <span className="sm-stack sm-pair sm-paytype-text">
            <span className="sm-type-body-strong">Pay in full</span>
            <span className="sm-type-body sm-paytype-sub">
              {formatUsd(pifWholesale)} — one charge today.
            </span>
          </span>
          <span className="sm-center sm-paytype-check" aria-hidden="true">
            <CheckIcon size={12} strokeWidth={3} />
          </span>
        </button>
        {canFinance && (
          <button
            type="button"
            className="sm-focus-ring sm-paytype sm-paytype-row"
            aria-pressed={isFinance}
            onClick={selectFinance}
          >
            <span className="sm-center sm-paytype-icon">
              <CalendarClockIcon size={18} aria-hidden="true" />
            </span>
            <span className="sm-stack sm-pair sm-paytype-text">
              <span className="sm-type-body-strong">Finance</span>
              <span className="sm-type-body sm-paytype-sub">
                {formatUsd(financeWholesale)} — up to {longestTerm} months.
              </span>
            </span>
            <span className="sm-center sm-paytype-check" aria-hidden="true">
              <CheckIcon size={12} strokeWidth={3} />
            </span>
          </button>
        )}
      </div>

      {entry.payment.type === 'finance' ? (
        <div className="sm-stack sm-finance-block sm-finance-block-dense">
          <div className="sm-stack sm-field">
            <span className="sm-type-body sm-label">Payment duration</span>
            <TermChipRow
              options={durationOptions}
              selected={entry.payment.durationMonths}
              onSelect={(durationMonths) => {
                if (entry.payment.type !== 'finance') return;
                setCartEntryPayment(cartIndex, { ...entry.payment, durationMonths });
              }}
              ariaLabel="Duration"
            />
          </div>

          <FieldFrame
            label="Down payment"
            labelAux="Minimum"
            htmlFor="sm-chat-down-payment"
            error={showErrors && downPaymentError ? downPaymentError : undefined}
            help={`At least ${formatUsd(minDown)} (${minDownPaymentLabel(financeLimits.value)}) is required to finance.`}
          >
            <div className="sm-input-wrap">
              <span className="sm-type-body sm-input-currency-prefix">$</span>
              <input
                id="sm-chat-down-payment"
                type="text"
                className="sm-control sm-input sm-input-currency"
                inputMode="decimal"
                autoComplete="off"
                value={entry.payment.downPaymentInput}
                aria-invalid={showErrors && downPaymentError !== null ? true : undefined}
                onInput={(e) => {
                  if (entry.payment.type !== 'finance') return;
                  setCartEntryPayment(cartIndex, {
                    ...entry.payment,
                    downPaymentInput: (e.currentTarget as HTMLInputElement).value,
                  });
                  showPaymentErrors.value = false;
                }}
              />
            </div>
          </FieldFrame>

          {monthly !== null ? (
            <div className="sm-monthly-preview sm-monthly-preview-dense">
              <span className="sm-stack sm-pair">
                <span className="sm-type-eyebrow">Estimated</span>
                <span className="sm-type-body sm-muted sm-monthly-preview-title">
                  Monthly payment
                </span>
              </span>
              <span className="sm-stack sm-pair-end sm-monthly-preview-amount">
                <span className="sm-type-price sm-amount">
                  <PriceDisplay amount={monthly} unit="/mo" />
                </span>
                <span className="sm-type-caption">
                  for {entry.payment.durationMonths} months · {formatUsd(line.downPayment ?? 0)}{' '}
                  down
                </span>
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
